<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { runtimeConfig } from '../platform/runtime'
import SimulationView from './SimulationView.vue'

const route = useRoute()
const isMobilePublic = runtimeConfig.mode === 'mobile-public'
const simulationSection = ref<HTMLElement | null>(null)

function scrollToSimulation() {
  simulationSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function scrollToSection(sectionId: string) {
  document.getElementById(sectionId)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  })
}

async function focusSimulation(queryFocus: unknown) {
  if (isMobilePublic || queryFocus !== 'simulation') return
  await nextTick()
  scrollToSimulation()
}

onMounted(() => {
  void focusSimulation(route.query.focus)
})

watch(
  () => route.query.focus,
  (queryFocus) => {
    void focusSimulation(queryFocus)
  }
)

const architectureLayers = [
  {
    index: '04',
    name: 'BA 管理软件',
    role: '看得见、管得住',
    detail: '统一监控设备状态、运行参数、趋势、报警与能耗，为管理和运维提供清晰界面。',
    examples: ['可视化监控', '报警与趋势', '报表与能耗']
  },
  {
    index: '03',
    name: '网络与通信',
    role: '让信息可靠流动',
    detail: '通过楼宇控制网络连接管理软件和各区域 DDC，完成数据上传、指令下发与系统联动。',
    examples: ['控制网络', '协议转换', '系统集成']
  },
  {
    index: '02',
    name: 'DDC 控制层',
    role: '现场自动控制核心',
    detail: 'DDC 安装在控制机柜内，采集现场信号并按控制逻辑完成运算、联锁和输出。',
    examples: ['机柜与供电', 'DDC / I/O', '控制逻辑']
  },
  {
    index: '01',
    name: '末端与机电设备',
    role: '感知现场、执行控制',
    detail: '传感器采集温度、压力、流量等数据；阀门和执行器调节空调、给排水等机电系统。',
    examples: ['传感器', '阀门与执行器', '机电设备']
  }
]

const functions = [
  { icon: '◉', title: '集中监控', text: '把分散的设备状态集中到一个平台，现场运行一目了然。' },
  { icon: '◇', title: '自动控制', text: '根据时间、环境和负荷自动启停与调节，减少人工操作。' },
  { icon: '!', title: '报警处置', text: '异常及时提醒并保留记录，帮助人员快速定位和处理问题。' },
  { icon: '↗', title: '趋势分析', text: '记录关键运行参数和变化趋势，为优化与故障判断提供依据。' },
  { icon: '▥', title: '能源管理', text: '掌握设备运行与用能情况，发现浪费并支持节能策略。' },
  { icon: '✓', title: '运维协同', text: '统一设备信息、运行记录与处置过程，提升维护效率。' }
]

const values = [
  { number: '01', title: '更舒适', text: '稳定控制温度、湿度和空气品质，改善建筑使用体验。' },
  { number: '02', title: '更节能', text: '按实际需求运行设备，减少无效运行和能源浪费。' },
  { number: '03', title: '更高效', text: '集中管理与自动控制，降低巡检和重复操作工作量。' },
  { number: '04', title: '更可靠', text: '及时发现异常、保留运行数据，降低故障扩大风险。' }
]
</script>

<template>
  <section class="hero home-hero">
    <div class="hero-copy">
      <p class="eyebrow">BUILDING AUTOMATION / BA SYSTEM</p>
      <h2 class="home-hero-title">
        <span>让建筑设备自动运行，</span><span>让项目一次达标交付，</span><span>让使用长久安心无忧。</span>
      </h2>
      <p>
        BA 楼宇自控系统连接空调、通风、给排水等机电设备，通过现场感知、DDC
        自动控制和上位软件集中管理，实现设计达标、竣工顺验、长期稳定运行，
        为建设方降低交付风险，为使用方带来舒适体验。
      </p>
      <div class="home-hero-tags" aria-label="BA 系统目标">
        <span>环境舒适</span><span>运行节能</span><span>管理高效</span><span>设备可靠</span>
      </div>
      <div class="home-hero-actions">
        <button v-if="!isMobilePublic" class="button button-primary" type="button" @click="scrollToSimulation">
          <span aria-hidden="true">01</span>
          进入运行仿真
        </button>
        <button
          class="button home-overview-link"
          type="button"
          @click="scrollToSection('home-architecture')"
        >
          <span aria-hidden="true">{{ isMobilePublic ? '01' : '02' }}</span>
          一张图看懂 BA
        </button>
        <button
          class="button home-overview-link"
          type="button"
          @click="scrollToSection('home-functions')"
        >
          <span aria-hidden="true">{{ isMobilePublic ? '02' : '03' }}</span>
          BA 能做什么
        </button>
        <button
          class="button home-overview-link"
          type="button"
          @click="scrollToSection('home-values')"
        >
          <span aria-hidden="true">{{ isMobilePublic ? '03' : '04' }}</span>
          为用户带来什么
        </button>
      </div>
    </div>
    <div class="hero-system" aria-label="DDC 与 BA 系统组成示意">
      <div class="system-ring system-ring-a"></div>
      <div class="system-ring system-ring-b"></div>
      <div class="system-core"><strong>DDC</strong><span>AUTOMATIC CONTROL</span></div>
      <div class="system-node node-a">机柜</div>
      <div class="system-node node-b">末端设备</div>
      <div class="system-node node-c">BA 软件</div>
      <div class="system-node node-d">应用案例</div>
    </div>
  </section>

  <section
    v-if="!isMobilePublic"
    id="home-simulation"
    ref="simulationSection"
    class="section-block home-simulation-section"
    aria-labelledby="home-simulation-title"
  >
    <div class="home-simulation-demo">
      <div class="home-simulation-demo-bar">
        <div><i aria-hidden="true"></i><strong id="home-simulation-title">新风空调运行仿真</strong></div>
        <span>输入条件 → DDC 运算 → 设备调节 → 环境反馈</span>
      </div>
      <div class="home-simulation-canvas">
        <SimulationView />
      </div>
    </div>

    <p class="home-simulation-disclaimer">
      演示说明：本仿真用于展示控制逻辑、动态调节过程与变化趋势，不作为具体项目的设备选型、负荷计算或现场 PID 整定依据。
    </p>
  </section>

  <section
    id="home-architecture"
    class="section-block home-architecture home-anchor-section"
    aria-labelledby="architecture-title"
  >
    <div class="home-section-heading">
      <div>
        <p class="eyebrow">SYSTEM ARCHITECTURE</p>
        <h2 id="architecture-title">一张图看懂 BA 系统架构</h2>
      </div>
      <p>现场数据逐层向上汇集，控制指令逐层向下执行，DDC 是现场自动控制的核心。</p>
    </div>

    <div class="architecture-flow">
      <article v-for="(layer, index) in architectureLayers" :key="layer.index" class="architecture-layer">
        <div class="architecture-index">{{ layer.index }}</div>
        <div class="architecture-title">
          <span>{{ layer.role }}</span>
          <h3>{{ layer.name }}</h3>
        </div>
        <p>{{ layer.detail }}</p>
        <div class="architecture-examples">
          <span v-for="item in layer.examples" :key="item">{{ item }}</span>
        </div>
        <div v-if="index < architectureLayers.length - 1" class="architecture-connector" aria-hidden="true">
          <span>数据 ↑</span><i></i><span>↓ 控制</span>
        </div>
      </article>
    </div>

    <div class="ddc-explainer">
      <span class="ddc-badge">DDC</span>
      <div>
        <strong>DDC 是 BA 系统的现场控制核心</strong>
        <p>机柜为 DDC 提供供电、保护和安装环境；末端设备提供输入与执行输出；BA 软件负责集中监控；实际案例说明这些能力如何落地。</p>
      </div>
    </div>
  </section>

  <section
    id="home-functions"
    class="section-block home-anchor-section"
    aria-labelledby="functions-title"
  >
    <div class="home-section-heading">
      <div><p class="eyebrow">CORE FUNCTIONS</p><h2 id="functions-title">BA 系统能做什么</h2></div>
      <p>从“看见运行”到“自动优化”，帮助建筑形成完整的监控、控制和管理闭环。</p>
    </div>
    <div class="home-function-grid">
      <article v-for="item in functions" :key="item.title" class="home-function-card">
        <span>{{ item.icon }}</span><div><h3>{{ item.title }}</h3><p>{{ item.text }}</p></div>
      </article>
    </div>
  </section>

  <section
    id="home-values"
    class="section-block home-value-section home-anchor-section"
    aria-labelledby="value-title"
  >
    <div class="home-section-heading">
      <div><p class="eyebrow">CUSTOMER VALUE</p><h2 id="value-title">最终为用户带来什么</h2></div>
      <p>技术架构不是目的，持续改善建筑的使用体验和运营质量才是价值。</p>
    </div>
    <div class="home-value-grid">
      <article v-for="item in values" :key="item.number">
        <span>{{ item.number }}</span><h3>{{ item.title }}</h3><p>{{ item.text }}</p>
      </article>
    </div>
  </section>
</template>
